import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  inject,
} from '@angular/core';
import { Auth } from '@freelancer/auth';
import type { RequestError } from '@freelancer/datastore';
import {
  Datastore,
  ORDER_BY_OPTIONS_UNICODE_CI,
  OrderByDirection,
} from '@freelancer/datastore';
import type {
  DiscoverCollection,
  DiscoverCollectionsCollection,
  EnterprisesCollection,
  PoolModel,
  PoolsCollection,
  PredefinedDiscoverCollection,
  PredefinedDiscoverCollectionsCollection,
  User,
  UsersCollection,
  UsersFollowCollection,
} from '@freelancer/datastore/collections';
import { Pool } from '@freelancer/datastore/collections';
import { LocalStorage } from '@freelancer/local-storage';
import { Assets } from '@freelancer/ui/assets';
import { trackById } from '@freelancer/ui/helpers';
import {
  IconColor,
  IconSize,
  flBird,
  flBookmarkV2,
  flHeartAltV2,
  flListBullet,
  flShowOutline,
  flSuitcaseOutline,
  flUserOutline,
  provideFlIcons,
} from '@freelancer/ui/icon';
import { LinkUnderline } from '@freelancer/ui/link';
import {
  QueryParamsHandling,
  TabsDirection,
  TabsSize,
} from '@freelancer/ui/tabs';
import { UI_CONFIG } from '@freelancer/ui/ui.config';
import { UiConfig } from '@freelancer/ui/ui.interface';
import { isDefined, toNumber } from '@freelancer/utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DiscoverCollectionTypeApi } from 'api-typings/users/users';
import type { Observable } from 'rxjs';
import { BehaviorSubject, combineLatest, firstValueFrom, of } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';

@UntilDestroy({ className: 'MyListsComponent' })
@Component({
  providers: [
    provideFlIcons({
      flBird,
      flBookmarkV2,
      flHeartAltV2,
      flListBullet,
      flShowOutline,
      flSuitcaseOutline,
      flUserOutline,
    }),
  ],
  standalone: false,
  selector: 'fl-my-lists',
  template: `
    <fl-scrollable-content class="MyListsScrollableContent">
      @if ((userCollectionLoading$ | flAsync) === false) {
        <!-- Talent pools and default user lists  -->
        @if (showBookmarkedPosts) {
          <div class="MyLists-listCollectionHeader">
            <p class="text-body-16 font-bold">
              @if (uiConfig.theme === 'deloitte') {
                <ng-container i18n="My List Widget Talent List Title">
                  Talent
                </ng-container>
              }
              @if (uiConfig.theme !== 'deloitte') {
                <ng-container i18n="My List Widget User List Title">
                  Users
                </ng-container>
              }
            </p>
          </div>
        }
        <fl-tabs
          [direction]="TabsDirection.COLUMN_RIGHT"
          [size]="TabsSize.XSMALL"
          [truncate]="true"
        >
          @if (showPoolLists$ | flAsync) {
            <ng-container ngProjectAs="fl-tab-item">
              @for (
                talentNetworkPool of talentNetworkPools$ | flAsync;
                track trackById($index, talentNetworkPool)
              ) {
                <fl-tab-item
                  flTrackingLabel="PoolsCollectionClick"
                  [flTrackingReferenceType]="'pool_id'"
                  [flTrackingReferenceId]="talentNetworkPool.id"
                  [iconName]="'flBird'"
                  [iconColor]="IconColor.SECONDARY"
                  [iconSize]="IconSize.SMALL"
                  [link]="'/lists/pool/' + talentNetworkPool.seoUrl"
                  [queryParamsHandling]="QueryParamsHandling.PRESERVE"
                  [titleText]="talentNetworkPool.displayName"
                ></fl-tab-item>
              }
              @if (insourcePool$ | flAsync; as insourcePool) {
                <fl-tab-item
                  flTrackingLabel="PoolsCollectionClick"
                  [flTrackingReferenceType]="'pool_id'"
                  [flTrackingReferenceId]="insourcePool.id"
                  [imageSrc]="insourcePoolImage"
                  [link]="'/lists/pool/' + insourcePool.seoUrl"
                  [queryParamsHandling]="QueryParamsHandling.PRESERVE"
                  [titleText]="insourcePool.displayName"
                ></fl-tab-item>
              }
            </ng-container>
          }
          @if (favoritesList$ | flAsync; as favoritesList) {
            <fl-tab-item
              flTrackingLabel="FavoritesCollectionClick"
              titleText="Favorites"
              i18n-titleText="My List Favorites Collection Title"
              [flTrackingReferenceType]="'collection_id'"
              [flTrackingReferenceId]="favoritesList.id"
              [iconColor]="IconColor.PRIMARY"
              [iconName]="'flHeartAltV2'"
              [iconSize]="IconSize.SMALL"
              [link]="'/lists/' + favoritesList.seoUrl"
              [preserveBackButton]="preserveBackButton"
              [queryParamsHandling]="QueryParamsHandling.PRESERVE"
            ></fl-tab-item>
          }
          @if (myHiresList$ | flAsync; as myHiresList) {
            <fl-tab-item
              flTrackingLabel="MyHiresCollectionClick"
              titleText="My Hires"
              i18n-titleText="My List My Hires Collection Title"
              [flTrackingReferenceType]="'collection_id'"
              [flTrackingReferenceId]="myHiresList.id"
              [iconName]="'flSuitcaseOutline'"
              [iconSize]="IconSize.SMALL"
              [link]="'/lists/' + myHiresList.seoUrl"
              [preserveBackButton]="preserveBackButton"
              [queryParamsHandling]="QueryParamsHandling.PRESERVE"
            ></fl-tab-item>
          }
          @if (myClientsList$ | flAsync; as myClientsList) {
            <ng-container ngProjectAs="fl-tab-item">
              @if (myClientsList.itemCount > 0) {
                <fl-tab-item
                  flTrackingLabel="MyClientsCollectionClick"
                  titleText="My Clients"
                  i18n-titleText="My List My Clients Collection Title"
                  [flTrackingReferenceType]="'collection_id'"
                  [flTrackingReferenceId]="myClientsList.id"
                  [iconName]="'flSuitcaseOutline'"
                  [iconSize]="IconSize.SMALL"
                  [link]="'/lists/' + myClientsList.seoUrl"
                  [preserveBackButton]="preserveBackButton"
                  [queryParamsHandling]="QueryParamsHandling.PRESERVE"
                ></fl-tab-item>
              }
            </ng-container>
          }
          @if (showFollowing$ | flAsync) {
            <fl-tab-item
              flTrackingLabel="FollowingCollectionClick"
              titleText="Following"
              i18n-titleText="Following Users Collection Title"
              [iconName]="'flUserOutline'"
              [iconSize]="IconSize.SMALL"
              [link]="'/users/employerWatchList.php'"
              [preserveBackButton]="preserveBackButton"
              [queryParamsHandling]="QueryParamsHandling.PRESERVE"
            ></fl-tab-item>
          }
          @if (recentlyViewedList$ | flAsync; as recentlyViewed) {
            <ng-container ngProjectAs="fl-tab-item">
              <fl-tab-item
                flTrackingLabel="RecentlyViewedCollectionClick"
                titleText="Recently Viewed"
                i18n-titleText="My List Recently Viewed Collection Title"
                [flTrackingReferenceType]="'collection_id'"
                [flTrackingReferenceId]="recentlyViewed.id"
                [iconName]="'flShowOutline'"
                [iconSize]="IconSize.SMALL"
                [link]="'/lists/' + recentlyViewed.seoUrl"
                [preserveBackButton]="preserveBackButton"
                [queryParamsHandling]="QueryParamsHandling.PRESERVE"
              ></fl-tab-item>
            </ng-container>
          }
          @for (
            customList of customListsToShow$ | flAsync;
            track trackById($index, customList);
            let isLast = $last
          ) {
            <ng-container ngProjectAs="fl-tab-item">
              <fl-tab-item
                flTrackingLabel="CustomCollectionClick"
                [flTrackingReferenceType]="'collection_id'"
                class="{{ isLast ? 'mb-xxsmall' : 'mb-none' }}"
                [flTrackingReferenceId]="customList.id"
                [iconName]="'flListBullet'"
                [iconSize]="IconSize.SMALL"
                [link]="'/lists/' + customList.seoUrl"
                [queryParamsHandling]="QueryParamsHandling.PRESERVE"
                [titleText]="customList.name"
              ></fl-tab-item>
            </ng-container>
          }
        </fl-tabs>
        @if (viewAllBottom && (showViewMore$ | flAsync)) {
          <div class="MyLists-viewMore">
            <fl-link
              flTrackingLabel="ViewMoreClick"
              i18n="My List Widget View More Text"
              [underline]="LinkUnderline.NEVER"
              (click)="viewMoreClick()"
            >
              View More
            </fl-link>
          </div>
        }
        <!-- Bookmarked group posts -->
        @if (showBookmarkedPosts) {
          @if (showHorizontalSeparator) {
            <fl-hr class="HorizontalLine"></fl-hr>
          }
          <div class="MyLists-listCollectionHeader">
            <p
              class="text-body-16 font-bold"
              i18n="My List Widget Group Posts List Title"
            >
              Group posts
            </p>
          </div>
          <fl-tabs
            [direction]="TabsDirection.COLUMN_RIGHT"
            [size]="TabsSize.XSMALL"
            [truncate]="true"
          >
            @if (groupPostsList$ | flAsync; as groupPosts) {
              <ng-container ngProjectAs="fl-tab-item">
                <fl-tab-item
                  flTrackingLabel="BookmarkedGroupPostsCollectionClick"
                  titleText="Bookmarks"
                  i18n-titleText="My List Groups Posts Collection Title"
                  [flTrackingReferenceType]="'collection_id'"
                  [flTrackingReferenceId]="groupPosts.id"
                  [iconName]="'flBookmarkV2'"
                  [iconSize]="IconSize.SMALL"
                  [link]="'/lists/' + groupPosts.seoUrl"
                  [preserveBackButton]="preserveBackButton"
                  [queryParamsHandling]="QueryParamsHandling.PRESERVE"
                ></fl-tab-item>
              </ng-container>
            }
          </fl-tabs>
        }
      } @else {
        <div class="MyLists-loadingState">
          <fl-spinner flTrackingLabel="My List Widget Loading"></fl-spinner>
        </div>
      }

      <!-- Loading state -->

      <!-- Error state -->
      @if (userCollectionError$ | flAsync; as error) {
        <fl-generic-error [error]="error"></fl-generic-error>
      }
    </fl-scrollable-content>
  `,
  styleUrls: ['./my-lists.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyListsComponent implements OnInit, OnChanges {
  private assets = inject(Assets);
  private auth = inject(Auth);
  private datastore = inject(Datastore);
  private localStorage = inject(LocalStorage);
  uiConfig = inject<UiConfig>(UI_CONFIG);

  IconSize = IconSize;
  IconColor = IconColor;
  LinkUnderline = LinkUnderline;
  QueryParamsHandling = QueryParamsHandling;
  TabsDirection = TabsDirection;
  TabsSize = TabsSize;
  trackById = trackById;

  @Input() showPoolLists$ = of(false);
  @Input() customListLimit = 8;
  @Input() viewAllBottom = false;
  @Input() preserveBackButton = true;
  @Input() showBookmarkedPosts = true;
  @Input() showHorizontalSeparator = true;

  user$: Observable<User>;
  insourcePool$: Observable<PoolModel>;
  insourcePoolImage: string;
  talentNetworkPools$: Observable<readonly PoolModel[]>;
  favoritesList$: Observable<PredefinedDiscoverCollection>;
  myHiresList$: Observable<PredefinedDiscoverCollection>;
  myClientsList$: Observable<PredefinedDiscoverCollection>;
  showFollowing$: Observable<boolean>;
  recentlyViewedList$: Observable<PredefinedDiscoverCollection>;
  groupPostsList$: Observable<PredefinedDiscoverCollection>;
  customLists$: Observable<readonly DiscoverCollection[]>;
  userCollectionLoading$: Observable<boolean>;
  userCollectionError$: Observable<
    | RequestError<EnterprisesCollection>
    | RequestError<PoolsCollection>
    | RequestError<DiscoverCollectionsCollection>
    | RequestError<PredefinedDiscoverCollectionsCollection>
    | undefined
  >;
  showViewMore$: Observable<boolean>;

  customListsToShow$: Observable<readonly DiscoverCollection[]>;

  private listViewLimitSubject$ = new BehaviorSubject<number>(
    this.customListLimit,
  );
  listViewLimit$ = this.listViewLimitSubject$.asObservable();

  private shouldShowInitialLoadingStateSubject$ = new BehaviorSubject<boolean>(
    true,
  );
  shouldShowInitialLoadingState$ =
    this.shouldShowInitialLoadingStateSubject$.asObservable();

  private VIEW_MORE_INCREMENT = 10;

  ngOnChanges(changes: SimpleChanges): void {
    if ('customListLimit' in changes && this.customListLimit !== undefined) {
      this.listViewLimitSubject$.next(this.customListLimit);
    }
  }

  ngOnInit(): void {
    const userId$ = this.auth.getUserId().pipe(map(userId => toNumber(userId)));

    const userDocument = this.datastore.document<UsersCollection>(
      'users',
      userId$,
    );

    const enterpriseIds$ = userDocument.valueChanges().pipe(
      map(user =>
        user.isDeloitteDcUser && user.enterpriseIds
          ? user.enterpriseIds
          : undefined,
      ),
      filter(isDefined),
    );

    const enterpriseCollection =
      this.datastore.collection<EnterprisesCollection>('enterprises', query =>
        this.showPoolLists$.pipe(
          map(showPoolLists =>
            showPoolLists
              ? query.where('id', 'in', enterpriseIds$)
              : query.null(),
          ),
        ),
      );

    /**
     * Create a mapping of enterprise names. Users should only have one enterprise,
     * but this should support multiple
     */
    const enterpriseNamesMap$ = enterpriseCollection
      .valueChanges()
      .pipe(
        map(enterprises =>
          enterprises?.length > 0
            ? new Map<string, boolean>(
                enterprises.map(enterprise => [enterprise.internalName, true]),
              )
            : undefined,
        ),
      );

    /**
     * Gets this the same way that the logo component gets enterprise logos,
     * but should be from the enterprise (or ideally the pool)
     */
    this.insourcePoolImage = this.assets.getAssetPath(
      'freelancer-enterprise-pool-icon.svg',
    );

    const isDeloitteDemo$ = this.localStorage
      .get('deloitteDemo')
      .pipe(map(isDeloitteDemo => !!isDeloitteDemo));

    const poolsCollection = this.datastore.collection<PoolsCollection>(
      'pools',
      query =>
        this.showPoolLists$.pipe(
          map(showPoolLists => (showPoolLists ? query : query.null())),
        ),
    );

    const favoritesListDocument =
      this.datastore.document<PredefinedDiscoverCollectionsCollection>(
        'predefinedDiscoverCollections',
        DiscoverCollectionTypeApi.LISTS_FAVORITES,
        { index: 'type' },
      );

    const myHiresListDocument =
      this.datastore.document<PredefinedDiscoverCollectionsCollection>(
        'predefinedDiscoverCollections',
        DiscoverCollectionTypeApi.LISTS_MY_HIRES,
        { index: 'type' },
      );

    const myClientsListDocument =
      this.datastore.document<PredefinedDiscoverCollectionsCollection>(
        'predefinedDiscoverCollections',
        DiscoverCollectionTypeApi.LISTS_MY_CLIENTS,
        { index: 'type' },
      );

    const followingList = this.datastore.collection<UsersFollowCollection>(
      'usersFollow',
      query => query.where('followedByUserId', '==', userId$),
    );

    const recentlyViewedListDocument =
      this.datastore.document<PredefinedDiscoverCollectionsCollection>(
        'predefinedDiscoverCollections',
        DiscoverCollectionTypeApi.LISTS_RECENTLY_VIEWED_USERS,
        { index: 'type' },
      );

    const groupPostsListDocument =
      this.datastore.document<PredefinedDiscoverCollectionsCollection>(
        'predefinedDiscoverCollections',
        DiscoverCollectionTypeApi.LISTS_GROUP_FEED_ITEMS,
        { index: 'type' },
      );

    const customListsCollection =
      this.datastore.collection<DiscoverCollectionsCollection>(
        'discoverCollections',
        query =>
          query
            .where('ownerId', '==', userId$)
            .where('type', '==', DiscoverCollectionTypeApi.LISTS_CUSTOM)
            .orderBy('name', OrderByDirection.ASC, ORDER_BY_OPTIONS_UNICODE_CI)
            .limit(
              this.listViewLimitSubject$.pipe(
                map(limit => limit + this.VIEW_MORE_INCREMENT + 1),
              ),
            ),
      );

    // Fetch enterprise insource internal talent directories
    this.insourcePool$ = combineLatest([
      this.showPoolLists$,
      enterpriseNamesMap$,
      poolsCollection.valueChanges(),
    ]).pipe(
      map(([showPoolLists, enterpriseNamesMap, pools]) =>
        showPoolLists
          ? pools.find(
              pool =>
                enterpriseNamesMap?.has(pool.name) &&
                pool.isInsourceEnabled &&
                !pool.isTalentNetwork,
            )
          : undefined,
      ),
      filter(isDefined),
      map(pool => {
        // TODO: T267853 - Replace with a display name mapping
        return pool.id === Pool.DELOITTE_DC
          ? { ...pool, displayName: 'Deloitte Gig Workers' }
          : pool;
      }),
    );

    // Fetch enterprise talent networks
    this.talentNetworkPools$ = combineLatest([
      this.showPoolLists$,
      isDeloitteDemo$,
      poolsCollection.valueChanges(),
    ]).pipe(
      map(([showPoolLists, isDeloitteDemo, pools]) => {
        return pools.filter(pool => {
          // only filter by valid pools
          if (
            !showPoolLists ||
            !pool.isTalentNetwork ||
            pool.isInsourceEnabled
          ) {
            return false;
          }

          // enable deloitte pool only if deloitteDemo is enabled
          if (!isDeloitteDemo && Pool.DELOITTE_FTN === pool.id) {
            return false;
          }

          return true;
        });
      }),
    );

    this.favoritesList$ = favoritesListDocument.valueChanges();
    this.myHiresList$ = myHiresListDocument.valueChanges();
    this.myClientsList$ = myClientsListDocument.valueChanges();
    this.showFollowing$ = followingList.valueChanges().pipe(
      map(following => following.length > 0),
      startWith(false),
    );
    this.recentlyViewedList$ = recentlyViewedListDocument.valueChanges();
    this.groupPostsList$ = groupPostsListDocument.valueChanges();
    this.customLists$ = customListsCollection.valueChanges();

    this.customListsToShow$ = combineLatest([
      this.customLists$,
      this.listViewLimit$,
    ]).pipe(map(([customLists, limit]) => customLists.slice(0, limit)));

    const enterpriseError$ = enterpriseCollection.status$.pipe(
      map(status => status.error),
    );

    const poolError$ = poolsCollection.status$.pipe(
      map(status => status.error),
    );

    const favoritesListError$ = favoritesListDocument.status$.pipe(
      map(status => status.error),
    );

    const myHiresListError$ = myHiresListDocument.status$.pipe(
      map(status => status.error),
    );

    const myClientsListError$ = myClientsListDocument.status$.pipe(
      map(status => status.error),
    );

    const recentlyViewedListError$ = recentlyViewedListDocument.status$.pipe(
      map(status => status.error),
    );

    const groupPostsListError$ = groupPostsListDocument.status$.pipe(
      map(status => status.error),
    );

    const customListsError$ = customListsCollection.status$.pipe(
      map(status => status.error),
    );

    const enterpriseLoading$ = enterpriseCollection.status$.pipe(
      map(status => !status.error && !status.ready),
    );

    const poolsLoading$ = combineLatest([
      poolsCollection.status$,
      poolsCollection.valueChanges().pipe(startWith(undefined)),
    ]).pipe(
      map(([status, pools]) => !status.error && (!status.ready || !pools)),
    );

    const favoritesListLoading$ = combineLatest([
      this.favoritesList$.pipe(startWith(undefined)),
      favoritesListDocument.status$,
    ]).pipe(
      map(
        ([favoritesList, status]) =>
          !status.error && (!status.ready || !favoritesList),
      ),
    );

    const myHiresListLoading$ = combineLatest([
      this.myHiresList$.pipe(startWith(undefined)),
      myHiresListDocument.status$,
    ]).pipe(
      map(
        ([myHiresList, status]) =>
          !status.error && (!status.ready || !myHiresList),
      ),
    );

    const myClientsListLoading$ = combineLatest([
      this.myClientsList$.pipe(startWith(undefined)),
      myClientsListDocument.status$,
    ]).pipe(
      map(
        ([myClientsList, status]) =>
          !status.error && (!status.ready || !myClientsList),
      ),
    );

    const recentlyViewedListLoading$ = combineLatest([
      this.recentlyViewedList$.pipe(startWith(undefined)),
      recentlyViewedListDocument.status$,
    ]).pipe(
      map(
        ([recentlyViewedList, status]) =>
          !status.error && (!status.ready || !recentlyViewedList),
      ),
    );

    const groupPostsListLoading$ = combineLatest([
      this.groupPostsList$.pipe(startWith(undefined)),
      groupPostsListDocument.status$,
    ]).pipe(
      map(
        ([groupPostsList, status]) =>
          !status.error && (!status.ready || !groupPostsList),
      ),
    );

    const customListsLoading$ = combineLatest([
      customListsCollection.status$,
      this.customLists$.pipe(startWith(undefined)),
      this.shouldShowInitialLoadingState$,
    ]).pipe(
      map(([status, customCollection, shouldShowLoading]) =>
        shouldShowLoading
          ? !status.error && (!status.ready || !customCollection)
          : false,
      ),
    );

    this.userCollectionLoading$ = combineLatest([
      enterpriseLoading$,
      poolsLoading$,
      favoritesListLoading$,
      myHiresListLoading$,
      myClientsListLoading$,
      recentlyViewedListLoading$,
      groupPostsListLoading$,
      customListsLoading$,
    ]).pipe(
      map(loadingStates => loadingStates.some(ls => ls)),
      startWith(true),
    );

    this.userCollectionError$ = combineLatest([
      enterpriseError$,
      poolError$,
      favoritesListError$,
      myHiresListError$,
      myClientsListError$,
      recentlyViewedListError$,
      groupPostsListError$,
      customListsError$,
    ]).pipe(map(errorStates => errorStates.find(es => es !== undefined)));

    this.showViewMore$ = combineLatest([
      this.customLists$,
      this.listViewLimit$,
    ]).pipe(map(([customLists, limit]) => customLists.length > limit));
  }

  viewMoreClick(): void {
    this.shouldShowInitialLoadingStateSubject$.next(false);

    firstValueFrom(
      this.listViewLimitSubject$.asObservable().pipe(untilDestroyed(this)),
    ).then(limit => {
      this.listViewLimitSubject$.next(limit + this.VIEW_MORE_INCREMENT);
    });
  }
}
